import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { parseLocalDate, toLocalDateString } from '../utils/payrollPeriodLocal';
import { formatCompanyDate, formatCompanyDateLocale } from '../utils/companyDateFormat';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function daySelectable(ymd: string, min?: string, max?: string): boolean {
  if (min && ymd < min) return false;
  if (max && ymd > max) return false;
  return true;
}

/**
 * Date picker with a month grid whose weeks start on `weekStartsOn` (0=Sunday … 6=Saturday),
 * matching company Attendance / week_start_day settings. Prefer over &lt;input type="date"&gt; when
 * calendar week alignment matters (browser-native pickers follow locale, not app settings).
 */
export function SingleDayCalendarPicker({
  value,
  onChange,
  weekStartsOn,
  min,
  max,
  disabled = false,
  className = '',
  buttonClassName = '',
  popoverClassName = '',
  id,
  'aria-label': ariaLabel,
}: {
  value: string;
  onChange: (ymd: string) => void;
  weekStartsOn: number;
  min?: string;
  max?: string;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  /** e.g. z-[70] when inside a high z-index modal */
  popoverClassName?: string;
  id?: string;
  'aria-label'?: string;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });

  const updatePopoverPosition = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPopoverPos({ top: r.bottom + 4, left: r.left });
  }, []);

  const viewDate = useMemo(() => {
    if (value && String(value).trim()) {
      try {
        return parseLocalDate(value);
      } catch {
        return new Date();
      }
    }
    if (min && String(min).trim()) {
      try {
        return parseLocalDate(min);
      } catch {
        return new Date();
      }
    }
    return new Date();
  }, [value, min]);

  const [monthCursor, setMonthCursor] = useState(() => new Date(viewDate.getFullYear(), viewDate.getMonth(), 1));

  useEffect(() => {
    setMonthCursor(new Date(viewDate.getFullYear(), viewDate.getMonth(), 1));
  }, [viewDate]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePopoverPosition();
  }, [open, updatePopoverPosition]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePopoverPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePopoverPosition]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const year = monthCursor.getFullYear();
  const month = monthCursor.getMonth();
  const monthLabel = formatCompanyDateLocale(monthCursor, { month: 'long', year: 'numeric' });

  const firstCell = new Date(year, month, 1);
  while (firstCell.getDay() !== weekStartsOn) {
    firstCell.setDate(firstCell.getDate() - 1);
  }
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(firstCell);
    d.setDate(firstCell.getDate() + i);
    cells.push(d);
  }

  const dayNames = useMemo(() => {
    const out: string[] = [];
    for (let i = 0; i < 7; i++) out.push(DAY_NAMES[(weekStartsOn + i) % 7]);
    return out;
  }, [weekStartsOn]);

  const displayLabel = value
    ? (() => {
        try {
          return formatCompanyDate(parseLocalDate(value));
        } catch {
          return value;
        }
      })()
    : 'Select date';

  const popoverContent = open && (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Calendar"
      style={{
        position: 'fixed',
        top: popoverPos.top,
        left: popoverPos.left,
        zIndex: 10000,
      }}
      className={`rounded-lg border border-gray-200 bg-white p-3 shadow-lg ${popoverClassName}`}
    >
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setMonthCursor(new Date(year, month - 1, 1))}
          className="rounded p-1 text-gray-600 hover:bg-gray-100"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="text-sm font-medium text-gray-800">{monthLabel}</span>
        <button
          type="button"
          onClick={() => setMonthCursor(new Date(year, month + 1, 1))}
          className="rounded p-1 text-gray-600 hover:bg-gray-100"
          aria-label="Next month"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center text-xs">
        {dayNames.map((name) => (
          <div key={name} className="py-1 font-medium text-gray-500">
            {name}
          </div>
        ))}
        {cells.map((d) => {
          const ymd = toLocalDateString(d);
          const selectable = daySelectable(ymd, min, max);
          const isCurrentMonth = d.getMonth() === month;
          const isSelected = value === ymd;
          return (
            <button
              key={ymd}
              type="button"
              disabled={!selectable}
              onClick={() => {
                if (!selectable) return;
                onChange(ymd);
                setOpen(false);
              }}
              className={`
                    rounded py-1.5 text-sm
                    ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-800'}
                    ${!selectable ? 'cursor-not-allowed opacity-40' : 'hover:bg-gray-100'}
                    ${isSelected ? 'bg-blue-600 font-medium text-white hover:bg-blue-600' : ''}
                  `}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={`inline-block ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        id={id}
        disabled={disabled}
        aria-label={ariaLabel ?? 'Choose date'}
        aria-expanded={open}
        onClick={() => {
          if (disabled) return;
          setOpen((o) => !o);
        }}
        className={`inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-2 py-1.5 text-left text-sm text-gray-900 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 ${buttonClassName}`}
      >
        <Calendar className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
        <span>{displayLabel}</span>
      </button>
      {typeof document !== 'undefined' && popoverContent ? createPortal(popoverContent, document.body) : null}
    </div>
  );
}
